import { observer } from 'mobx-react-lite'
import {
    Button,
    Select,
    Switch,
    Input,
    InputNumber,
    Form,
    notification,
    Upload,
    Image,
    Space,
    Tag, Row, Col, Alert,
    Dropdown, Modal
} from 'antd'
import type { MenuProps } from 'antd'
import i from '../../../translations/i'
import React, { useEffect } from 'react'
import { Titles } from '../../../layouts/Titles'
import 'react-textarea-emoji/dist/index.css'
import {
    CheckCircleFilled,
    DeleteOutlined,
    EditOutlined,
    GiftOutlined,
    MoreOutlined,
    NotificationOutlined,
    PauseCircleFilled,
    PlusOutlined,
} from '@ant-design/icons'
import controller from './push_templates.controller'
import { useParams } from 'react-router-dom'
import { ErrorByPricingPlan } from '../../../components/ErrorByPricingPlan'
import { storeLocal } from '../../../helpers/stores'
import { SelectEntity } from "../../../helpers/select_entity/SelectEntity";
import __select from "../../../helpers/select_entity/__select.controller";
import { PushFunnelStats, PUSH_FUNNEL_ENABLED, templatesFunnel } from './push_funnel.controller'
import { percent, PushFunnelSummary } from './PushFunnel'
import { CardList, EMPTY_VALUE, ListCard, ListChip, ListMedia, ListMetricProps } from '../../../components/card_list/CardList'
import { getPrice, getPriceShort } from '../../../helpers/price'
import { PushBonusFields } from './PushBonus'
import './pushes.less'

const templateTypeLabel = (type: string): string => i.t(`pushes:templates_types.${type}`, { defaultValue: type })

// Цифры шаблона — за период, выбранный в воронке выше: шаблон уходит много раз, в отличие от рассылки
const TemplateCard = observer(({ template }: { template: any }) => {
    const stats = templatesFunnel.stats(template.id)
    const image = template.image && template.image.uri ? `${template.image.base ?? ''}${template.image.uri}` : null
    const value = (render: (stats: PushFunnelStats) => string) => stats ? render(stats) : EMPTY_VALUE

    const ordersHint = stats
        ? [
            i.t('pushes:funnel.ordersHint'),
            stats.conversion !== null ? `${i.t('pushes:funnel.conversion')}: ${percent(stats.conversion)}` : null,
            stats.canceled > 0 ? i.t('pushes:funnel.canceled', { value: stats.canceled }) : null,
        ].filter(Boolean).join('. ')
        : i.t('pushes:funnel.ordersHint')

    // Сетка в 12 колонок, как у рассылок: две строки по три ячейки
    const metrics: ListMetricProps[] = PUSH_FUNNEL_ENABLED
        ? [
            { value: value(stats => stats.sends.toLocaleString()), label: i.t('pushes:funnel.sends_templates'), hint: i.t('pushes:templates.periodHint'), span: 4 },
            { value: value(stats => stats.delivered.toLocaleString()), label: i.t('pushes:funnel.delivered'), hint: i.t('pushes:funnel.deliveredHint'), span: 4 },
            { value: value(stats => stats.taps.toLocaleString()), label: i.t('pushes:funnel.taps'), hint: i.t('pushes:funnel.tapsHint'), span: 4 },
            { value: value(stats => percent(stats.ctr)), label: i.t('pushes:funnel.ctr'), hint: i.t('pushes:funnel.ctrHint'), span: 4 },
            { value: value(stats => stats.orders.toLocaleString()), label: i.t('pushes:funnel.orders'), hint: ordersHint, span: 4 },
            {
                // В ячейке — целыми, точная сумма в подсказке
                value: value(stats => getPriceShort(stats.revenue)),
                label: i.t('pushes:funnel.ordersSum'),
                hint: stats ? `${getPrice(stats.revenue)}. ${i.t('pushes:funnel.ordersSumHint')}` : i.t('pushes:funnel.ordersSumHint'),
                span: 4,
            },
        ]
        : [{
            value: i.t('pushes:funnel.soon'),
            label: i.t('pushes:list.soonMetrics'),
            hint: i.t('pushes:funnel.soonText'),
            span: 12,
            dashed: true,
            tone: 'warning',
        }]

    const confirmDelete = () => Modal.confirm({
        title: i.t('def:confirms.delete'),
        content: i.t('def:confirms.this_action'),
        okText: i.t('def:actions.remove'),
        okButtonProps: { danger: true },
        cancelText: i.t('def:no'),
        onOk: () => controller.delete(template.id),
    })

    const menu: MenuProps['items'] = [
        { key: 'edit', icon: <EditOutlined />, label: <a href={`/pushes/templates/${template.id}`}>{i.t('def:actions.edit')}</a> },
        { key: 'delete', icon: <DeleteOutlined />, danger: true, label: i.t('def:actions.remove'), onClick: confirmDelete },
    ]

    return (
        <ListCard
            href={`/pushes/templates/${template.id}`}
            media={image ? <ListMedia image={image} /> : undefined}
            title={template.name || template.title}
            titleHint={template.name || template.title}
            text={template.title}
            muted={!template.enabled}
            chips={<>
                {template.enabled
                    ? <ListChip tone="success" icon={<CheckCircleFilled />}>{i.t('def:states.included')}</ListChip>
                    : <ListChip icon={<PauseCircleFilled />}>{i.t('def:states.turned')}</ListChip>
                }
                <ListChip>{templateTypeLabel(template.type)}</ListChip>
                {template.bonus_amount > 0 &&
                    <ListChip tone="primary" icon={<GiftOutlined />}>{i.t('pushes:bonus.chip', { amount: template.bonus_amount, days: template.bonus_days })}</ListChip>
                }
            </>}
            metrics={metrics}
            metricColumns={12}
            metricWidth={36}
            actions={
                <Dropdown menu={{ items: menu }} trigger={['click']} placement="bottomRight">
                    <Button type="text" shape="circle" icon={<MoreOutlined />} aria-label={i.t('def:actions.actions')} />
                </Dropdown>
            }
        />
    )
})

export const PushesTemplates = observer(() => {

    useEffect(() => {
        controller.getTemplates()
        templatesFunnel.getSummary()
        templatesFunnel.getTemplateItems()
    }, [])

    const buttons = [
        { title: i.t('def:actions.back'), href: `/pushes`, type: 'dashed' },
        { title: i.t('def:actions.create'), href: `/pushes/templates/0`, type: 'primary' },
    ]

    return(
        <>
            <Titles
                title={i.t('pushes:templates.title')}
                buttons={buttons}
            />

            <div className="pu-page">
                <PushFunnelSummary funnel={templatesFunnel} />

                <CardList
                    title={i.t('pushes:templates.list')}
                    count={controller.templates.length}
                    items={controller.templates}
                    rowKey={(template: any) => template.id}
                    renderItem={(template: any) => <TemplateCard template={template} />}
                    empty={{
                        icon: <NotificationOutlined />,
                        title: i.t('pushes:templates.empty'),
                        text: i.t('pushes:templates.emptyHint'),
                        action: <Button type="primary" icon={<PlusOutlined />} href="/pushes/templates/0">{i.t('pushes:templates.create')}</Button>,
                    }}
                />
            </div>
        </>
    )
})

const GetRenderImage = observer(({ el, key_element }: any) => {

    return(
        <Form.Item
            label={el.label}
        >
            <Space>
                {controller.template[key_element] && controller.template[key_element].base &&
                    <>
                        <Image
                            width={110}
                            src={`${controller.template[key_element].base}${controller.template[key_element].uri}`}
                        />
                        <Button onClick={() => controller.deleteImageFromTemplate()}>{i.t('def:actions.remove')}</Button>
                    </>
                }
                <Upload
                    beforeUpload={(info) => {
                        if(info.size > 500000) {
                            notification.error({
                                message: i.t('def:errors.fileSizeTitle'),
                                description: i.t('def:errors.fileSizeDescription')
                            });
                            return false
                        }
                        const isPNG = info.type === 'image/png';
                        if (!isPNG) {
                            notification.error({
                                message: i.t('def:errors.fileTypeTitle'),
                                description: i.t('def:errors.fileTypeDescription')
                            });
                            return false
                        }
                        controller.uploadTemplate(info)
                        return false
                    }}
                    listType="picture-card"
                    className="photo-uploader"
                    maxCount={1}
                >
                    <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8 }}>{i.t('def:image.upload')}</div>
                    </div>
                </Upload>
            </Space>
        </Form.Item>
    )
})

const GetRenderText = observer(({ el, key_element }: any) => {

    return(
        <>
            <Form.Item
                label={el.label}
            >
                <Input.TextArea
                    value={controller.template[key_element]}
                    onChange={(e) => controller.template[key_element] = e.target.value}
                    required={el.required}
                />
            </Form.Item>
        </>
    )
})

const GetRenderNumber = observer(({ el, key_element }: any) => {

    return(
        <>
            <Form.Item
                label={el.label}
                help={el.props.comment}
            >
                <InputNumber
                    max={el.props.max}
                    min={el.props.min}
                    value={controller.template[key_element]}
                    onChange={(e) => e >= 0 ? controller.template[key_element] = e : 12}
                    required={el.required}
                />
            </Form.Item>
        </>
    )
})

const GetRenderString = observer(({ el, key_element }: any) => {

    return(
        <>
            <Form.Item
                label={el.label}
            >
                <Input
                    value={controller.template[key_element]}
                    onChange={(e) => controller.template[key_element] = e.target.value}
                    required={el.required}
                />
            </Form.Item>
        </>
    )
})

const GetRenderSelect = observer(({ el, key_element }: any) => {

    return(
        <>
            <Form.Item
                label={el.label}
            >
                <Select
                    options={el.enums}
                    value={controller.template[key_element]}
                    defaultValue={el.enums[0] && el.enums[0].value ? el.enums[0].value : null}
                    onChange={(e) => controller.template[key_element] = e}
                    defaultActiveFirstOption={true}
                />
            </Form.Item>
        </>
    )
})

const GetRenderByType = observer(({ key_element, element }: any) => {

    return(
        <>
            {element.type === 'image' && <GetRenderImage el={element} key_element={key_element} />}
            {element.type === 'text' && <GetRenderText el={element} key_element={key_element} />}
            {element.type === 'number' && <GetRenderNumber el={element} key_element={key_element} />}
            {element.type === 'string' && <GetRenderString el={element} key_element={key_element} />}
            {element.type === 'select' && <GetRenderSelect el={element} key_element={key_element} />}
        </>
    )
})

export const PushesTemplate = observer(() => {

    const { id } = useParams<any>()

    useEffect(() => {
        if(id > 0) controller.getTemplate(id)
    }, [])

    const buttons = [
        { title: i.t('def:actions.back'), href: `/pushes/templates`, type: 'dashed', plan: 2 }
    ]

    return(
        <>
            <Titles
                title={i.t('pushes:pushes.push')}
                buttons={buttons}
            />

            {(!controller.template.id || controller.template.id === 0) &&
                <Select
                    style={{ width: '300px' }}
                    options={[
                      {value: 'birthday', label: i.t('pushes:templates_types.birthday')},
                      {value: 'unpaid_orders', label: i.t('pushes:templates_types.unpaid_orders')},
                      {value: 'custom', label: i.t('pushes:templates_types.custom')},
                      {value: 'expire_bonus_points', label: i.t('pushes:templates_types.expire_bonus_points')},
                      {value: 'payment_canceled', label: i.t('pushes:templates_types.payment_canceled')},
                      {value: 'order_after_hours', label: i.t('pushes:templates_types.order_after_hours')},
                      {value: 'recurring', label: i.t('pushes:templates_types.recurring')},
                      {value: 'low_balance', label: i.t('pushes:templates_types.low_balance')},
                      {value: 'abandoned_cart', label: i.t('pushes:templates_types.abandoned_cart')},
                    ]}
                    onChange={(e) => controller.getForm(e)}
                />
            }

            {controller.template.type &&
                <form onSubmit={(e) => controller.save(e)}>
                    <Form.Item
                        label={controller.template.enabled ? i.t(`def:states.included`) : i.t(`def:states.turned`)}
                    >
                        <Switch
                            size="small"
                            checked={controller.template.enabled}
                            onChange={(e) => controller.template.enabled = e}
                        />
                    </Form.Item>

                    {controller.vars.length
                        ? <>
                            <Alert
                                type="success"
                                message={<Row>
                                    <Col span={24}>
                                        <p>{i.t('def:push_templates_vars')}</p>
                                    </Col>
                                    <Col span={24}>
                                        {controller.vars
                                            ? <>
                                                {controller.vars.map((var_el: any) => (
                                                    <p><Tag>{`{{${var_el.key}}}`}</Tag> - {var_el.value}</p>
                                                ))}
                                            </>
                                            : <></>
                                        }
                                    </Col>
                                </Row>}
                            />
                            <br/>
                        </>
                        : <></>
                    }

                    {Object.keys(controller.form).map((el: any) => {
                        return <GetRenderByType key_element={el} element={controller.form[el]} />
                    })}

                  {controller.template.type === 'custom' || controller.template.type === 'birthday' || controller.template.type === 'recurring'
                    ? <Col span={24}>
                      <SelectEntity
                        type={__select.select.type}
                        element={__select.select.value}
                        item_element={__select.select.element}
                      />
                    </Col>
                    : <></>
                  }

                    <Form.Item label={i.t('pushes:bonus.title')}>
                        <div style={{ maxWidth: 640 }}>
                            <PushBonusFields value={controller.template} template />
                        </div>
                    </Form.Item>


                    <ErrorByPricingPlan
                        plan={2}
                        content={
                            <Button
                                type="primary"
                                htmlType="submit"
                                disabled={!storeLocal.get('pricing_plan') || storeLocal.get('pricing_plan') < 2}
                            >
                                {i.t('def:actions.save')}
                            </Button>
                        }
                    />

                </form>
            }
        </>
    )
})